import { getUint16Value, getUint8Value } from "@/utils/ByteUtils";

import * as _ from "lodash";
import {
  calculateComBoxHumidityPd,
  calculateAbsoluteHumidity,
  calculateMixingRatio,
  getHumidityValue,
  getComBoxHumidityUnitFormatRes,
} from "./ComBoxHumidityUnit";
import { getComBoxHumidityUnit } from "../repository/SettingsRepository";

export enum ComBoxSensorType {
  EMPTY = "EMPTY",
  TELAIRE = "TELAIRE",
  SHT3X = "SHT3X",
  RADON = "RADON",
  // Carries the same data as TELAIRE/SHT3X
  BLE_SENSOR_TYPE_1 = "BLE_SENSOR_TYPE_1",
  BLE_SENSOR_TYPE_3 = "BLE_SENSOR_TYPE_3",
  // Firmware type flag 10; carries the same temperature/humidity data as type 1.
  BLE_SENSOR_TYPE_5 = "BLE_SENSOR_TYPE_5",
  // Legacy serialized value kept for backward compatibility.
  BLE_SENSOR_TYPE_MATERIAL = "BLE_SENSOR_TYPE_MATERIAL",
  UNKNOWN = "UNKNOWN",
}

export const ROOM_SENSOR_INDEX = 4;

/** Total number of sensor slots: 4 external (0-3) + 1 room (4) + 4 extended external (5-8) */
export const TOTAL_SENSOR_COUNT = 9;

/**
 * Sentinel coded-resistance value (both bytes 0xFF) meaning the material sensor
 * reported no reading — analogous to the 255/255 sentinel for temp/humidity sensors.
 */
export const MATERIAL_SENSOR_NO_READING = 0xffff;

/** Returns true for the BLE resistance sensor, including legacy serialized enum values. */
export function isBleResistanceSensorType(sensorType: ComBoxSensorType | string | undefined): boolean {
  return (
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_3 ||
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_MATERIAL
  );
}

export function getSensorTypeFromFlag(flag: number) {
  switch (flag) {
    case 0:
      return ComBoxSensorType.EMPTY;
    case 1:
      return ComBoxSensorType.TELAIRE;
    case 2:
      return ComBoxSensorType.SHT3X;
    case 3:
      return ComBoxSensorType.RADON;
    case 6:
      return ComBoxSensorType.BLE_SENSOR_TYPE_1;
    case 8:
      return ComBoxSensorType.BLE_SENSOR_TYPE_3;
    case 10:
      return ComBoxSensorType.BLE_SENSOR_TYPE_5;
  }

  return ComBoxSensorType.UNKNOWN;
}

export function formatSensorType(vue: Vue, sensorType: ComBoxSensorType) {
  switch (sensorType) {
    case ComBoxSensorType.EMPTY:
      return vue.$t("comBox.sensorType.empty").toString();
    case ComBoxSensorType.TELAIRE:
      return vue.$t("comBox.sensorType.telaire").toString();
    case ComBoxSensorType.SHT3X:
      return vue.$t("comBox.sensorType.sht3x").toString();
    case ComBoxSensorType.RADON:
      return vue.$t("comBox.sensorType.radon").toString();
    case ComBoxSensorType.BLE_SENSOR_TYPE_1:
      return vue.$t("comBox.sensorType.bluetoothType1").toString();
    case ComBoxSensorType.BLE_SENSOR_TYPE_5:
      return vue.$t("comBox.sensorType.bluetoothType5").toString();
    case ComBoxSensorType.BLE_SENSOR_TYPE_3:
    case ComBoxSensorType.BLE_SENSOR_TYPE_MATERIAL:
      return vue.$t("comBox.sensorType.bluetoothType3").toString();
  }

  return vue.$t("comBox.sensorType.unknow").toString();
}

export interface ComBoxSensorData {
  sensorIndex: number;
  temperature?: number;
  relativeHumidity?: number;
  absoluteHumidity?: number;
  mixingRatio?: number;
  codedResistance?: number;
  resistance?: number;
  resistanceDigits?: number;
  radonValue?: number;
  powerConsumption?: number;
  encodedPower?: number;
}

/** Decodes BCD-encoded material sensor resistance; returns null for malformed values. */
export function decodeResistance(codedResistance: number): number | null {
  const exp = codedResistance & 0x0f;
  const bcdMantissa = codedResistance >>> 4;

  const hundreds = (bcdMantissa >>> 8) & 0x0f;
  const tens = (bcdMantissa >>> 4) & 0x0f;
  const ones = bcdMantissa & 0x0f;

  if (exp > 0x0c || hundreds > 9 || tens > 9 || ones > 9) {
    return null;
  }

  let value = hundreds * 100 + tens * 10 + ones;

  for (let i = 0; i < exp; i++) {
    value *= 10;
  }

  return value;
}

/**
 * Converts resistance in Ohms to the graph-ready resistance digits value.
 * The calibrated polynomial output is rounded to a whole number — resistance
 * digits are always displayed as integers (no decimals) — and clamped to the
 * displayable 0–100 range (matching the fixed graph axis).
 */
export function toResistanceDigitsFromOhms(ohms: number): number {
  const x = ohms / 1_000_000;
  const logx = Math.log10(x);

  const digits =
    x <= 1
      ? 7.84 * logx ** 3 + 30.659 * logx ** 2 + 0.0605 * logx + 22.975
      : -0.00038212 * logx ** 3 + 0.0224009 * logx ** 2 - 3.324 * logx + 20.419;

  return Math.round(Math.min(100, Math.max(0, digits)));
}

export function isIndexOfRoomSensor(sensorIndex: number) {
  return sensorIndex === ROOM_SENSOR_INDEX;
}

export function getSensorData(
  sensorType: ComBoxSensorType,
  sensorIndex: number,
  sensorDataArrayIndex: number,
  sensorDataArray: Uint8Array
): ComBoxSensorData {
  switch (sensorType) {
    case ComBoxSensorType.TELAIRE:
    case ComBoxSensorType.SHT3X:
    case ComBoxSensorType.BLE_SENSOR_TYPE_1:
    case ComBoxSensorType.BLE_SENSOR_TYPE_5: {
      const encodedTemp = getUint8Value(sensorDataArray, sensorDataArrayIndex);
      const encodedHumidity = getUint8Value(
        sensorDataArray,
        sensorDataArrayIndex + 1
      );

      if (encodedTemp == 255 && encodedHumidity == 255) {
        return { sensorIndex: sensorIndex };
      } else {
        const temperature = encodedTemp / 2 - 20;
        const relativeHumidity = encodedHumidity / 2;

        if (temperature >= 107 || relativeHumidity > 100) {
          return { sensorIndex: sensorIndex };
        }

        const pd = calculateComBoxHumidityPd(temperature, relativeHumidity);
        const absoluteHumidity = calculateAbsoluteHumidity(temperature, pd);
        const mixingRatio = calculateMixingRatio(pd);

        return {
          sensorIndex: sensorIndex,
          temperature: temperature,
          relativeHumidity: relativeHumidity,
          mixingRatio: mixingRatio,
          absoluteHumidity: absoluteHumidity,
        };
      }
    }
    case ComBoxSensorType.BLE_SENSOR_TYPE_3:
    case ComBoxSensorType.BLE_SENSOR_TYPE_MATERIAL: {
      const codedResistance = getUint16Value(sensorDataArray, sensorDataArrayIndex);

      // 0xFFFF signals "no reading" — discard it entirely (no codedResistance stored),
      // mirroring the 255/255 sentinel handling for temperature/humidity sensors.
      if (codedResistance === MATERIAL_SENSOR_NO_READING) {
        return { sensorIndex };
      }

      const resistance = decodeResistance(codedResistance);

      if (resistance === null) {
        return {
          sensorIndex,
          codedResistance,
        };
      }

      return {
        sensorIndex,
        codedResistance,
        resistance,
        resistanceDigits: toResistanceDigitsFromOhms(resistance),
      };
    }
    case ComBoxSensorType.RADON:
      return {
        sensorIndex: sensorIndex,
        radonValue: getUint16Value(sensorDataArray, sensorDataArrayIndex),
      };
  }

  return {
    sensorIndex: sensorIndex,
  };
}

export function readSensorData(
  sensorConfig: ComBoxSensorType[],
  sensorData: Uint8Array
) {
  return sensorConfig.map((sensorType, sensorIndex) =>
    getSensorData(sensorType, sensorIndex, sensorIndex * 2, sensorData)
  );
}

enum ComBoxSensorDataKeyValue {
  AVERAGE,
  MIN,
  MAX,
}

export function getKeyValue(
  keyValue: ComBoxSensorDataKeyValue,
  data: ComBoxSensorData[],
  callback: (comBoxSensorData: ComBoxSensorData) => number | undefined
) {
  const filtered = data
    .filter((sensorData) => !!sensorData)
    .map((sensorData) => callback(sensorData))
    .filter((value) => value !== undefined);

  if (filtered.length === 0) {
    return undefined;
  }

  switch (keyValue) {
    case ComBoxSensorDataKeyValue.MAX:
      return _.max(filtered);
    case ComBoxSensorDataKeyValue.MIN:
      return _.min(filtered);
    case ComBoxSensorDataKeyValue.AVERAGE:
      return _.mean(filtered);
  }
}

export function getMinMaxAvg(
  data: ComBoxSensorData[],
  callback: (comBoxSensorData: ComBoxSensorData) => number | undefined
) {
  return {
    min: getKeyValue(ComBoxSensorDataKeyValue.MIN, data, callback),
    max: getKeyValue(ComBoxSensorDataKeyValue.MAX, data, callback),
    avg: getKeyValue(ComBoxSensorDataKeyValue.AVERAGE, data, callback),
  };
}

export function formatTemperature(vue: Vue, temperature: number | undefined) {
  if (temperature === undefined) {
    return "-";
  }

  return vue
    .$t("common.formatTemperature", [
      temperature.toLocaleString(undefined, { maximumFractionDigits: 1 }),
    ])
    .toString();
}

export function formatHumidity(vue: Vue, comBoxSensorData: ComBoxSensorData) {
  if (!comBoxSensorData) {
    return null;
  }

  const humidityUnit = getComBoxHumidityUnit();
  const humidity = getHumidityValue(humidityUnit, comBoxSensorData);

  if (humidity === undefined) {
    return null;
  }

  return vue
    .$t(getComBoxHumidityUnitFormatRes(humidityUnit), [
      humidity.toLocaleString(undefined, { maximumFractionDigits: 1 }),
    ])
    .toString();
}

/** Formats a resistance-digits value for display (dashes when unavailable, e.g. non-material sensors). */
export function formatResistanceDigits(vue: Vue, resistanceDigits: number | undefined) {
  if (resistanceDigits === undefined) {
    return "-";
  }

  return vue
    .$t("dryingGraph.formatResistanceDigits", [
      resistanceDigits.toLocaleString(undefined, { maximumFractionDigits: 0 }),
    ])
    .toString();
}

/**
 * Number of data bytes a sensor entry carries inside the `sensorDataExt` record.
 *
 * Every sensor known to the firmware carries exactly 2 data bytes, so an unknown
 * type (a sensor generation this build doesn't know yet) is assumed to carry 2 as
 * well. Returning 0 there would leave the record walk misaligned and make it read
 * the following bytes as sensor entries.
 */
export function getSensorDataLengthFromSensorType(sensorType: ComBoxSensorType) {
  switch (sensorType) {
    case ComBoxSensorType.EMPTY:
      return 0;

    default:
      return 2;
  }
}